import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

// How the primary navigation is presented on mobile/tablet (< lg):
//  - 'drawer' : hamburger button opens the slide-in sidebar (default, classic)
//  - 'bottom' : a fixed bottom navigation bar with the top-level destinations
export type MobileNavMode = 'drawer' | 'bottom'

interface SidebarStore {
  isOpen: boolean // For mobile sheet (not persisted)
  isCollapsed: boolean // For desktop sidebar (persisted)
  mobileNavMode: MobileNavMode // User preference (persisted)
  // Controls the top-bar workspace switcher dropdown. Shared so the sidebar's
  // "Current Workspace" section header can open the same switcher (not persisted).
  workspaceSwitcherOpen: boolean
  onOpenChange: (open: boolean) => void
  close: () => void
  toggle: () => void
  toggleCollapse: () => void
  setMobileNavMode: (mode: MobileNavMode) => void
  setWorkspaceSwitcherOpen: (open: boolean) => void
}

export const useSidebarStore = create<SidebarStore>()(
  persist(
    (set) => ({
      isOpen: false,
      // New users get a collapsed sidebar by default; once they toggle,
      // the choice is remembered via localStorage.
      isCollapsed: true,
      // Default to the bottom navigation bar on mobile. Users who previously
      // picked the classic drawer keep their choice (it's persisted).
      mobileNavMode: 'bottom',
      workspaceSwitcherOpen: false,
      onOpenChange: (open) => set({ isOpen: open }),
      close: () => set({ isOpen: false }),
      toggle: () => set((state) => ({ isOpen: !state.isOpen })),
      toggleCollapse: () => set((state) => ({ isCollapsed: !state.isCollapsed })),
      setMobileNavMode: (mode) => set({ mobileNavMode: mode }),
      setWorkspaceSwitcherOpen: (open) => set({ workspaceSwitcherOpen: open }),
    }),
    {
      name: 'sidebar-preferences',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ isCollapsed: state.isCollapsed, mobileNavMode: state.mobileNavMode }),
    }
  )
)
